// MotionPageLoader.js - Motion-based page loading system for Ghost theme
import React from 'react';
import { createRoot } from 'react-dom/client';
import PageLoadingProgressMotion from './PageLoadingProgressMotion.jsx';

export class MotionPageLoader {
  constructor(options = {}) {
    this.options = {
      duration: options.duration || 2500,
      customMessage: options.customMessage || 'Loading Potred...',
      showOnNavigation: options.showOnNavigation !== false,
      ...options
    };
    
    this.isLoading = false;
    this.root = null;
    this.container = null;
    this.autoHideSuppressed = true;
    
    if (this.options.showOnNavigation) {
      this.bindNavigationEvents();
    }
  }

  bindNavigationEvents() {
    // Show loading on page navigation
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.href && !link.href.startsWith('#') && link.target !== '_blank') {
        // Only show loading for internal navigation (but NOT for homepage if already on homepage)
        if (link.href.startsWith(window.location.origin)) {
          // Don't show loading when clicking logo to go to homepage if already on homepage
          const currentPath = window.location.pathname;
          const linkPath = new URL(link.href).pathname;
          if (currentPath === '/' && linkPath === '/') {
            return; // Skip loading animation for same-page navigation
          }
          this.setAutoHideSuppressed(false);
          this.show();
        }
      }
    });

    // Hide loading when page loads
    window.addEventListener('load', () => {
      if (this.autoHideSuppressed) {
        return;
      }
      setTimeout(() => {
        if (!this.autoHideSuppressed) {
          this.hide();
        }
      }, 100);
    });

    // Handle back/forward navigation
    window.addEventListener('popstate', () => {
      this.show();
    });

    // Hide loading on DOMContentLoaded as fallback
    document.addEventListener('DOMContentLoaded', () => {
      if (this.autoHideSuppressed) {
        return;
      }
      setTimeout(() => {
        if (!this.autoHideSuppressed) {
          this.hide();
        }
      }, 200);
    });
  }

  show(message = null, progress = 0) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.createContainer();
    this.renderComponent(message, progress);
  }

  hide() {
    if (!this.isLoading) return;
    
    // The component handles its own exit animation
    this.isLoading = false;
    if (this.root && this.container) {
      // Let the component animate out first
      setTimeout(() => {
        this.cleanup();
      }, 500); // Wait for exit animation
    }
  }

  createContainer() {
    if (this.container) return;
    
    this.container = document.createElement('div');
    this.container.id = 'potred-motion-loader';
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.right = '0';
    this.container.style.bottom = '0';
    this.container.style.zIndex = '9999';
    this.container.style.pointerEvents = 'none';
    
    document.body.appendChild(this.container);
  }

  renderComponent(customMessage = null, progress = 0) {
    if (!this.container) return;

    if (!this.root) {
      this.root = createRoot(this.container);
    }

    this.root.render(React.createElement(PageLoadingProgressMotion, {
      isLoading: this.isLoading,
      progress: progress,
      customMessage: customMessage || this.options.customMessage,
      onComplete: () => {
        this.hide();
      }
    }));
  }

  updateProgress(percentage, message) {
    if (!this.isLoading) return;
    this.renderComponent(message, percentage);
  }

  cleanup() {
    if (this.root) {
      try {
        this.root.unmount();
      } catch (error) {
        console.warn('Error unmounting motion loader:', error);
      }
      this.root = null;
    }
    
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }

  setAutoHideSuppressed(suppress = true) {
    this.autoHideSuppressed = suppress;
  }

  // Manual control methods
  showLoading() {
    this.show();
  }

  hideLoading() {
    this.hide();
  }

  // Show loading for a specific duration
  showForDuration(duration = 2000) {
    this.show();
    setTimeout(() => {
      this.hide();
    }, duration);
  }

  destroy() {
    this.cleanup();
    
    // Remove event listeners
    document.removeEventListener('click', this.bindNavigationEvents);
    window.removeEventListener('load', this.bindNavigationEvents);
    window.removeEventListener('popstate', this.bindNavigationEvents);
    document.removeEventListener('DOMContentLoaded', this.bindNavigationEvents);
  }
}

export default MotionPageLoader;